import { SpaceBackdrop } from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { useLayoutSearchParams, usePathname, useRouter } from "@-/com.npmjs.expo-router";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { BackHandler, Platform, View } from "@-/com.npmjs.react-native";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLearnSummaryPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { showErrorToast } from "@-/dev.improve.haniverse.components.ui.failure-toast";
import {
  releaseRatingLatch,
  tryAcquireRatingLatch,
} from "@-/dev.improve.haniverse.lib.cards.review-latch";
import {
  createLearnCardSessionId,
  type LearnCardActionReceipt,
  type LearnCardActionSnapshot,
  type LearnCardExitDirection,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  type LearnDeckHistoryEdge,
  type LearnDeckHistoryRestore,
  type LearnDeckState,
  redoLearnDeckHistoryEdge,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnCardDestinationResolution } from "@-/dev.improve.haniverse.lib.learn.destination";
import { preparePageNavigatorQueries } from "@-/dev.improve.haniverse.lib.navigator.page";
import { isDeadlineExceededError, withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { PrimaryControlsStripTitle } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.title";
import { useLevels } from "@-/dev.improve.haniverse.routes.nav.app._lib.level";
import { findGraphemeLessonLevel } from "@-/dev.improve.haniverse.lib.level";
import { useVoiceStudyController } from "@-/dev.improve.haniverse.lib.voice-study";
import {
  LearnLayoutContext,
  type LearnSummaryDeckEntry,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  LearnMapCardCovered,
  LearnMapCardsBackdrop,
  useIsLearnMapOpen,
  useLearnCardsPathname,
  useLearnMapModeSelector,
  useOpenLearnMap,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { isLearnMapPathname } from "@-/dev.improve.haniverse.routes.nav.app.learn.map.navigator";
import { LearnDeckOperationsProvider } from "#learn-deck-operations-provider";
import { useLearnDeckOperations } from "#learn-deck-operations-context";
import { LearnDeckHistoryContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { usePushLearnDeckHistoryEntry } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.navigator";
import { LearnFlashcardDeck } from "#learn-card-deck";
import { LearnReviewRechargeBar } from "#learn-review-recharge-bar";
import { useLearnReviewRechargeGate } from "#learn-review-recharge-gate";
import { useLearnFlashcardStageTopInsets } from "#use-learn-card-stage-top-insets";
import { VisualActivityProvider } from "@-/dev.improve.haniverse.lib.visual-activity";
import { LearnDeckEntryNotFound } from "#learn-deck-entry-not-found";
import {
  LearnDeckQueueHeadObserver,
  LearnDeckQueueHeadStatus,
  type LearnDeckQueueHead,
} from "#learn-deck-queue-head";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import {
  learnDeckBrowserForward,
  subscribeLearnDeckBrowserTraversal,
} from "#learn-deck-browser-forward";
import {
  isLearnDeckQueueHeadParams,
  learnActiveDeckCardForEntry,
  learnDeckEntryFromParams,
  learnDeckParamsForEntry,
  type LearnDeckEntry,
} from "#learn-deck-entry";

/**
 * Bound for each step of a history restore: an undo or redo write, and the restored card’s
 * queries. Local writes and reads settle in milliseconds; this is the same 15s the restore’s
 * card preparation always allowed, long enough for a first network read of reference data.
 */
const LEARN_DECK_HISTORY_STEP_DEADLINE_MS = 15_000;
type Frame = {
  edge: LearnDeckHistoryEdge | null;
  id: string;
  route?: LearnCardDestinationResolution;
  state: LearnDeckState;
};
const PATH = "/(nav)/(app)/learn/deck" as const;
const first = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);
function entryForState(state: LearnDeckState, mode: "manual" | "voice"): LearnDeckEntry {
  const { item } = state.current;
  if (item.kind === "slide") {
    return { face: "front", kind: "slide", slideSlug: item.slideSlug, view: "study" };
  }
  if (item.kind === "quiz") {
    return {
      face: "front",
      kind: "quiz",
      quizSlug: item.quizSlug,
      source: item.source,
      view: "study",
    };
  }
  return item.kind === "lesson"
    ? {
        face: state.face,
        grapheme: item.grapheme,
        kind: "lesson",
        ...(item.part === undefined ? {} : { part: item.part }),
        ...(item.pinyin === undefined ? {} : { pinyin: item.pinyin }),
        view: state.view,
      }
    : {
        contextSentence: item.contextSentence ?? null,
        drillType: item.drillType,
        face: state.face,
        grapheme: item.grapheme,
        kind: "review",
        mode,
        view: state.view,
      };
}
const identity = (state: LearnDeckState) =>
  state.current.presentation.occurrence + ":" + state.face;

const LEARN_DECK_PARAM_KEYS = [
  "face",
  "historySession",
  "historyStep",
  "kind",
  "mode",
  "morpheme",
  "part",
  "pinyin",
  "contextSentence",
  "quizSlug",
  "quizSource",
  "slideSlug",
  "type",
  "view",
] as const;
type LearnDeckHostParams = Record<(typeof LEARN_DECK_PARAM_KEYS)[number], string | undefined>;

/** Keep the deck's route inputs local even when another tab publishes global search params. */
function useLearnDeckHostParams(isLegacySlide: boolean) {
  const routeParams = useLayoutSearchParams<Record<string, string | string[]>>();
  const pathname = usePathname();
  const next: LearnDeckHostParams = {
    contextSentence: first(routeParams.contextSentence),
    face: first(routeParams.face),
    historySession: first(routeParams.historySession),
    historyStep: first(routeParams.historyStep),
    kind: isLegacySlide ? "slide" : first(routeParams.kind),
    mode: first(routeParams.mode),
    morpheme: first(routeParams.morpheme),
    part: first(routeParams.part),
    pinyin: first(routeParams.pinyin),
    quizSlug: first(routeParams.quizSlug),
    quizSource: first(routeParams.quizSource),
    slideSlug: first(
      routeParams.slideSlug ?? (isLegacySlide ? routeParams["slide-slug"] : undefined),
    ),
    type: first(routeParams.type),
    view: first(routeParams.view),
  };
  const [retained, setRetained] = useState(next);
  // Learn's map opens over the cards: its routes carry none of the deck's inputs.
  const isLearnPath =
    (pathname === "/learn" || pathname.startsWith("/learn/")) && !isLearnMapPathname(pathname);
  // useLayoutSearchParams returns a new merged object on every render. Compare only the
  // scalar inputs consumed here; unrelated route params must not churn history callbacks.
  if (isLearnPath && LEARN_DECK_PARAM_KEYS.some((key) => retained[key] !== next[key])) {
    setRetained(next);
    return next;
  }
  return retained;
}

/** The navigator contains lightweight history entries; one deck stays mounted above it. */
export function LearnDeckHost() {
  // Keep the provider's child stable. Navigation and readiness belong to the inner host;
  // they must not rebuild the same query-option graph in both history and deck controllers.
  return useMemo(
    () => (
      <LearnDeckOperationsProvider>
        <LearnDeckHostContent />
        <LearnDeckRechargeSlot />
      </LearnDeckOperationsProvider>
    ),
    [],
  );
}

/** Persistent chrome: card preparation must never delay the recharge track's first paint. */
function LearnDeckRechargeSlot() {
  const gate = useLearnReviewRechargeGate();
  // Beneath Learn's map the bar stays with the cards it belongs to, hidden under the card that
  // slides over it on the way there.
  const pathname = useLearnCardsPathname();
  const isVisible =
    pathname.endsWith("/learn") ||
    pathname.endsWith("/learn/summary") ||
    pathname.endsWith("/learn/deck") ||
    pathname.includes("/learn/lesson/slide/");
  return <>{gate !== null && isVisible && <LearnDeckRecharge gate={gate} pathname={pathname} />}</>;
}

function LearnDeckRecharge({
  gate,
  pathname,
}: {
  gate: NonNullable<ReturnType<typeof useLearnReviewRechargeGate>>;
  pathname: string;
}) {
  const { progressHeight, progressTop } = useLearnFlashcardStageTopInsets();
  const [activeDeckCard] = useContextSelectors(LearnLayoutContext, (c) => c.activeDeckCard);
  const focused = useIsFocused();
  return (
    <VisualActivityProvider active={focused}>
      <LearnMapCardCovered className="absolute inset-0 z-20">
        <LearnReviewRechargeBar
          currentCardKind={
            pathname.includes("/learn/lesson/slide/") ? "slide" : (activeDeckCard?.kind ?? "review")
          }
          gate={gate}
          height={progressHeight}
          top={progressTop}
        />
      </LearnMapCardCovered>
    </VisualActivityProvider>
  );
}

type LearnDeckHostSession = {
  entry: LearnDeckEntry;
  id: string;
  /**
   * `preview`: created behind the summary for the head of the study queue and never shown on the
   * deck route, so it still shows its first card's front; nobody can have touched it. `deck`:
   * opened on the deck route, where the learner reveals, rates and explores it. Only a preview is
   * ever shown behind the summary or opened by Start.
   */
  role: "deck" | "preview";
  /** Set when the summary card opened this session; its first card can Undo back there. */
  summaryEntry: LearnSummaryDeckEntry | null;
};

function LearnDeckHostContent() {
  // While Learn's map is open, the route the cards showed: they stay as they were beneath it.
  const pathname = useLearnCardsPathname();
  const isMapOpen = useIsLearnMapOpen();
  const isMapHome = useLearnMapModeSelector((mode) => mode.isMapHome) ?? false;
  const isLegacySlide = pathname.includes("/learn/lesson/slide/");
  const params = useLearnDeckHostParams(isLegacySlide);
  const focused = useIsFocused();
  const queryClient = useQueryClient();
  const { navigatorOptions } = useLearnDeckOperations();
  const [summaryCardHeight, summaryDeckEntryRef, setHasSummaryPreview] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.summaryCardHeight,
    (c) => c.summaryDeckEntryRef,
    (c) => c.setHasSummaryPreview,
  );
  const isDeck = pathname.endsWith("/learn/deck") || isLegacySlide;
  const isSummary = pathname.endsWith("/learn/summary") || pathname.endsWith("/learn");
  // The scoped pathname retains deck/summary geometry while hidden. Ancestor focus is the
  // single signal that activates both studying and the larger preload budget on tab return.
  const sessionParam = first(params.historySession);
  const [session, setSession] = useState<LearnDeckHostSession | null>(null);
  const acknowledgedSession = useRef<string | null>(null);

  // ---- The head of the study queue: what the summary previews and Start opens ----
  const [queueHead, setQueueHead] = useState<LearnDeckQueueHead>({ status: "pending" });
  const onQueueHead = useCallback((next: LearnDeckQueueHead) => {
    setQueueHead((previous) =>
      previous.status === next.status &&
      (next.status !== "success" ||
        (previous.status === "success" && previous.entry === next.entry))
        ? previous
        : next,
    );
  }, []);
  const headEntry = queueHead.status === "success" ? queueHead.entry : undefined;
  // A preview is shown until the queue is known to have moved on from its card: a refetch in
  // flight (or a failed one) keeps what the summary card is covering.
  const isPreviewShown =
    session !== null &&
    session.role === "preview" &&
    (headEntry === undefined ||
      (headEntry !== null && identityEntry(headEntry) === identityEntry(session.entry)));
  // Start names no card (`LEARN_DECK_QUEUE_HEAD_PARAMS`): it opens the card the summary showed, or,
  // when it showed none, the head of the queue. Decided once per request and latched, so promoting
  // the preview or a refetch of the queue can never change which card a press opened.
  const isQueueHeadRequest =
    isDeck && sessionParam === undefined && isLearnDeckQueueHeadParams(params);
  const queueHeadCandidate = isQueueHeadRequest
    ? isPreviewShown && session !== null
      ? session.entry
      : (headEntry ?? null)
    : null;
  const [queueHeadEntry, setQueueHeadEntry] = useState<LearnDeckEntry | null>(null);
  if (queueHeadEntry !== null && !isQueueHeadRequest) {
    setQueueHeadEntry(null);
  }
  if (queueHeadEntry === null && queueHeadCandidate !== null) {
    setQueueHeadEntry(queueHeadCandidate);
  }
  // The deck route's card, parsed once at the boundary. `null` = the params don't describe a card
  // (a stale or hand-edited deep link, or Start's card is not resolved yet).
  const routeEntry = useMemo(() => learnDeckEntryFromParams(params), [params]);
  const requestedEntry = isQueueHeadRequest ? (queueHeadEntry ?? queueHeadCandidate) : routeEntry;
  // Only something that needs the head observes it: the summary (to keep its preview current, or
  // to build its first one) and an unresolved Start. Studying never re-runs the resolution.
  const needsQueueHead = isSummary
    ? focused || !isPreviewShown
    : isQueueHeadRequest && requestedEntry === null;

  // Behind the summary, preview the head of the queue in a session nobody has used. A session that
  // was on the deck route is dropped here, whatever it shows (a revealed back, a later card, its
  // info view): the summary never covers it and Start never reopens it. On 2026-10-06 a reused
  // session kept the revealed back of the very card the summary previewed, and Start waited for a
  // front that never came.
  const previewEntry = isSummary && headEntry != null && !isPreviewShown ? headEntry : null;
  useEffect(() => {
    if (!isSummary || session?.role !== "deck") {
      return;
    }
    startTransition(() => setSession(null));
  }, [isSummary, session]);
  useEffect(() => {
    if (previewEntry === null || session?.role === "deck") {
      return;
    }
    // Warm the card's queries when the device is idle, so the preview mounts without suspending.
    // Fail open: the card loads its own data, and nothing waits on the preview.
    let cancelled = false;
    const handle = requestIdleCallback(() => {
      void preparePageNavigatorQueries(queryClient, navigatorOptions(previewEntry).queries)
        .catch((error: unknown) => {
          logger(import.meta.location).warn("Learn deck: preview preparation failed", { error });
        })
        .finally(() => {
          if (!cancelled) {
            startTransition(() =>
              setSession({
                entry: previewEntry,
                id: createLearnCardSessionId(),
                role: "preview",
                summaryEntry: null,
              }),
            );
          }
        });
    });
    return () => {
      cancelled = true;
      cancelIdleCallback(handle);
    };
  }, [navigatorOptions, previewEntry, queryClient, session?.role]);
  const hasSummaryPreview = isSummary && isPreviewShown;
  // Beneath the map home the summary card stands aside (`LearnSummaryHost`): the preview is the
  // cards, at the deck's own size, and it is the card that slides in when the learner leaves the
  // home, since leaving it opens the deck on this very card.
  const isHomePreview = hasSummaryPreview && isMapHome;
  useEffect(() => {
    setHasSummaryPreview(hasSummaryPreview);
  }, [hasSummaryPreview, setHasSummaryPreview]);
  useEffect(() => () => setHasSummaryPreview(false), [setHasSummaryPreview]);

  useEffect(() => {
    if (!isDeck) {
      return;
    }
    if (session !== null && sessionParam === session.id) {
      acknowledgedSession.current = session.id;
      return;
    }
    const entry = requestedEntry;
    if (entry === null) {
      // Rendered as `LearnDeckEntryNotFound` or `LearnDeckQueueHeadStatus` below; there is no card
      // to adopt yet.
      return;
    }
    // Only an explicit entry (no history token) can be the summary card handing over; a restored
    // history step belongs to whichever session recorded it.
    let summaryEntry: LearnSummaryDeckEntry | null = null;
    if (sessionParam === undefined) {
      summaryEntry = summaryDeckEntryRef.current;
      summaryDeckEntryRef.current = null;
    }
    // No history token means an explicit entry/deep link starts a new session, unless the session
    // already shows that card and was never on the deck route: the summary's preview, or the
    // session this very entry opened a commit ago.
    if (
      session !== null &&
      sessionParam === undefined &&
      acknowledgedSession.current !== session.id &&
      identityEntry(session.entry) === identityEntry(entry)
    ) {
      if (summaryEntry !== null || session.role === "preview") {
        setSession({
          ...session,
          role: "deck",
          summaryEntry: summaryEntry ?? session.summaryEntry,
        });
      }
      return;
    }
    setSession({
      entry,
      id: sessionParam ?? createLearnCardSessionId(),
      role: "deck",
      summaryEntry,
    });
  }, [isDeck, requestedEntry, session, sessionParam, summaryDeckEntryRef]);
  // A deck route whose params name no card, and which no session owns (its history token), can
  // never be adopted: say so instead of leaving the deck blank. A session that does own the route
  // keeps rendering it.
  const isEntryNotFound =
    isDeck &&
    !isQueueHeadRequest &&
    requestedEntry === null &&
    (session === null || sessionParam !== session.id);
  // Start's card is not known yet (the queue is still resolving, failed to, or is empty). The deck
  // shows that itself, with a way forward; Start already left.
  const isQueueHeadUnresolved = isQueueHeadRequest && requestedEntry === null;
  const queueHeadObserver = needsQueueHead ? (
    <LearnDeckQueueHeadObserver onChange={onQueueHead} />
  ) : null;
  if (session === null) {
    if (isEntryNotFound) {
      return <LearnDeckEntryNotFound />;
    }
    if (isQueueHeadUnresolved) {
      return (
        <>
          {queueHeadObserver}
          <LearnDeckQueueHeadStatus head={queueHead} />
        </>
      );
    }
    // On the deck route with a valid entry, the adoption effect above starts the session right
    // after this commit; the bare surface bridges that one commit.
    return isDeck ? (
      <View className="absolute inset-0 z-10 bg-muted" testID="learn-deck-adopting">
        <SpaceBackdrop />
      </View>
    ) : (
      queueHeadObserver
    );
  }
  // Route publication precedes the effect that adopts an explicit entry. Keep the previous
  // preview inactive in that intervening commit: its activation checkpoint must not replace
  // the newly requested card's params with the card it was warming off-screen.
  const matchesRequestedSession =
    sessionParam === session.id ||
    (sessionParam === undefined &&
      requestedEntry !== null &&
      identityEntry(session.entry) === identityEntry(requestedEntry));
  // The deck is Learn's current route, whether or not the Learn tab itself is in front.
  const isDeckRoute = isDeck && matchesRequestedSession;
  const deckSession = (
    <LearnDeckSession
      initialEntry={session.entry}
      // Beneath the map the deck is still the cards' screen, shown but not studied, as when the
      // Learn tab is in the background.
      isActive={isDeckRoute && focused && !isMapOpen}
      isLearnFocused={focused}
      isUnderMap={(isDeckRoute && isMapOpen) || isHomePreview}
      key={session.id}
      ownsToolbar={isDeckRoute || isHomePreview}
      params={params}
      previewHeight={hasSummaryPreview && !isHomePreview ? summaryCardHeight : null}
      sessionId={session.id}
      summaryEntry={session.summaryEntry}
    />
  );
  if (isEntryNotFound || isQueueHeadUnresolved) {
    // The existing session stays mounted (inactive) under the notice, so the summary preview and
    // its history survive leaving this dead end.
    return (
      <>
        {queueHeadObserver}
        {deckSession}
        {isEntryNotFound ? (
          <LearnDeckEntryNotFound />
        ) : (
          <LearnDeckQueueHeadStatus head={queueHead} />
        )}
      </>
    );
  }
  return (
    <>
      {queueHeadObserver}
      {deckSession}
    </>
  );
}
function identityEntry(entry: LearnDeckEntry) {
  return JSON.stringify(learnDeckParamsForEntry(entry));
}

function LearnDeckSession({
  initialEntry,
  isActive,
  isLearnFocused,
  isUnderMap,
  ownsToolbar,
  params,
  previewHeight,
  sessionId,
  summaryEntry,
}: {
  initialEntry: LearnDeckEntry;
  isActive: boolean;
  isLearnFocused: boolean;
  /**
   * Learn's map is open over this session: the deck route's, or the preview beneath the map home.
   * It is drawn as it is, leaving and returning with the cards.
   */
  isUnderMap: boolean;
  /** Publishes the toolbar's card controls: the deck route's session, or the map home's preview. */
  ownsToolbar: boolean;
  params: LearnDeckHostParams;
  previewHeight: number | null;
  sessionId: string;
  summaryEntry: LearnSummaryDeckEntry | null;
}) {
  const router = useRouter();
  const pushHistoryEntry = usePushLearnDeckHistoryEntry();
  const queryClient = useQueryClient();
  const { controller, navigate, navigatorOptions } = useLearnDeckOperations();
  const [setActiveDeckCard, setActiveDeckStep] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.setActiveDeckCard,
    (c) => c.setActiveDeckStep,
  );
  const frames = useRef<Frame[]>([]);
  const cursor = useRef(0);
  const nextId = useRef(0);
  const pendingEdge = useRef<LearnDeckHistoryEdge | null>(null);
  const routeStamp = useRef<string | null>(null);
  const busy = useRef(false);
  const needsRestore = useRef(false);
  const deferredNavigation = useRef(false);
  const pendingPush = useRef<{ ignored: Set<string | undefined>; target: string } | null>(null);
  const [retry, setRetry] = useState(0);
  const [, setRevision] = useState(0);
  const [pendingStep, setPendingStep] = useState<"back" | "forward" | null>(null);
  const isPending = pendingStep !== null;
  const [error, setError] = useState<string | null>(null);
  const [restore, setRestore] = useState<LearnDeckHistoryRestore | null>(null);
  const [shown, setShown] = useState<LearnDeckState | null>(null);
  const mode = initialEntry.kind === "review" ? initialEntry.mode : "manual";
  const voiceStudyController = useVoiceStudyController();
  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (mode === "manual") {
      voiceStudyController.stop();
    } else {
      // No-op while a session is starting or running.
      void voiceStudyController.start();
    }
  }, [isActive, mode, voiceStudyController]);
  const activeRef = useLatest(isActive);
  // The history frame the deck should show. Web reads it from the browser entry (each step is its
  // own pushed entry, so Back/Forward drive it). Native keeps it here: rewriting the route on every
  // reveal and rating changed the root navigation state, which made React scan every retained
  // tab for route consumers (~20 ms per reveal, ~40 ms per rating in Release).
  const [nativeTargetId, setNativeTargetId] = useState<string | undefined>(undefined);
  const targetId = Platform.OS === "web" ? first(params.historyStep) : nativeTargetId;
  const targetRef = useLatest(targetId);
  useEffect(
    () =>
      subscribeLearnDeckBrowserTraversal(() => {
        pendingPush.current = null;
        setRetry((r) => r + 1);
      }),
    [],
  );
  const expectPush = useCallback(
    (id: string) => {
      const ignored = pendingPush.current?.ignored ?? new Set<string | undefined>();
      ignored.add(targetRef.current);
      if (pendingPush.current !== null) {
        ignored.add(pendingPush.current.target);
      }
      pendingPush.current = { ignored, target: id };
    },
    [targetRef],
  );
  const levels = useLevels();
  const levelIndex =
    initialEntry.kind === "lesson"
      ? (findGraphemeLessonLevel(levels, initialEntry)?.index ?? 0)
      : 0;
  const href = useCallback(
    (frame: Frame) => ({
      params: {
        ...learnDeckParamsForEntry(entryForState(frame.state, mode)),
        historySession: sessionId,
        historyStep: frame.id,
      },
      pathname: PATH,
    }),
    [mode, sessionId],
  );
  /** Make `frame` the history target: a pushed browser entry on web, deck state on native. */
  const pointAt = useCallback(
    (frame: Frame) => {
      if (Platform.OS === "web") {
        pushHistoryEntry(href(frame).params);
      } else {
        setNativeTargetId(frame.id);
      }
    },
    [href, pushHistoryEntry],
  );

  const checkpoint = useCallback(
    (state: LearnDeckState) => {
      if (busy.current || !activeRef.current) {
        return;
      }
      const normalized = { ...state, transition: { phase: "idle" as const } };
      const previous = frames.current[cursor.current];
      if (previous && identity(previous.state) === identity(state)) {
        const viewChanged = previous.state.view !== state.view;
        previous.state = normalized;
        if (viewChanged) {
          setShown(normalized);
          if (Platform.OS === "web") {
            router.setParams(href(previous).params);
          }
        }
        return;
      }
      setShown(normalized);
      const edge =
        previous &&
        previous.state.current.presentation.occurrence !== state.current.presentation.occurrence
          ? pendingEdge.current
          : null;
      if (edge !== null) {
        pendingEdge.current = null;
      }
      const frame: Frame = { edge, id: String(nextId.current++), state: normalized };
      frames.current = [...frames.current.slice(0, cursor.current + 1), frame];
      cursor.current = frames.current.length - 1;
      setRevision((r) => r + 1);
      if (previous === undefined) {
        expectPush(frame.id);
        // The one route write of a session: it binds the deck route to this session
        // (`historySession`), so returning to the route keeps the session and a later explicit
        // entry for the same card starts a new one.
        router.setParams(href(frame).params);
        if (Platform.OS !== "web") {
          setNativeTargetId(frame.id);
        }
      } else if (!deferredNavigation.current) {
        expectPush(frame.id);
        pointAt(frame);
      }
    },
    [activeRef, expectPush, href, pointAt, router],
  );

  const onActionSaved = useCallback(
    (
      receipt: LearnCardActionReceipt,
      snapshot: LearnCardActionSnapshot,
      direction: LearnCardExitDirection,
    ) => {
      pendingEdge.current = { direction, pendingRedoOperationId: null, receipt, snapshot };
    },
    [],
  );
  const onRouteExit = useCallback(
    (state: LearnDeckState, route: LearnCardDestinationResolution) => {
      const frame: Frame = {
        edge: pendingEdge.current,
        id: String(nextId.current++),
        route,
        state: { ...state, transition: { phase: "idle" } },
      };
      pendingEdge.current = null;
      frames.current = [...frames.current.slice(0, cursor.current + 1), frame];
      cursor.current = frames.current.length - 1;
      routeStamp.current = frame.id;
      expectPush(frame.id);
      setRevision((r) => r + 1);
    },
    [expectPush],
  );
  useEffect(() => {
    if (!isActive && routeStamp.current !== null) {
      if (Platform.OS === "web") {
        router.setParams({ historySession: sessionId, historyStep: routeStamp.current });
      } else {
        setNativeTargetId(routeStamp.current);
      }
      routeStamp.current = null;
    }
  }, [isActive, router, sessionId]);

  const reconcile = useCallback(async () => {
    const pending = pendingPush.current;
    if (pending !== null) {
      if (pending.target === targetRef.current) {
        pendingPush.current = null;
      } else if (pending.ignored.has(targetRef.current)) {
        return;
      } else {
        pendingPush.current = null;
      }
    }
    const target = frames.current.findIndex((frame) => frame.id === targetRef.current);
    if (target === -1 || busy.current || (target === cursor.current && !needsRestore.current)) {
      return;
    }
    const latch = tryAcquireRatingLatch();
    if (latch === null) {
      deferredNavigation.current = true;
      return;
    }
    busy.current = true;
    setPendingStep(target > cursor.current ? "forward" : "back");
    setError(null);
    // The way the card Undo brings back left: the last action stepped back over. Forward steps
    // and steps within one card (a reveal) have none and land in place.
    let enterFrom: LearnCardExitDirection | null = null;
    try {
      while (true) {
        const desired = frames.current.findIndex((frame) => frame.id === targetRef.current);
        if (desired === -1 || desired === cursor.current) {
          break;
        }
        const backward = desired < cursor.current;
        const edgeFrame = frames.current[backward ? cursor.current : cursor.current + 1];
        if (edgeFrame === undefined) {
          break;
        }
        const edge = edgeFrame.edge;
        enterFrom = backward ? (edge?.direction ?? enterFrom) : null;
        // EVERY saved action is reversed and replayed, not only ratings: the next card is resolved
        // from enrollment and slide completion too, so stepping back over an accepted lesson or a
        // finished slide without reversing it would skip those cards on the way forward again.
        if (edge !== null) {
          // Each step holds the rating latch and the pending overlay, so each is bounded: a write
          // that never answers ends in the overlay’s Retry instead of a spinner over the deck.
          if (backward) {
            await withDeadline(
              {
                deadline: LEARN_DECK_HISTORY_STEP_DEADLINE_MS,
                operation: "Learn deck history undo",
              },
              () => controller.undo(edge.receipt),
            );
          } else {
            // Exactly once across Retry: a redo that timed out may still land, so its retry reuses
            // the same operation id and finds that row instead of rating the card a second time.
            await redoLearnDeckHistoryEdge({
              deadlineMs: LEARN_DECK_HISTORY_STEP_DEADLINE_MS,
              edge,
              persist: controller.persist,
            });
          }
        }
        cursor.current += backward ? -1 : 1;
        needsRestore.current = true;
      }
      const frame = frames.current[cursor.current];
      if (frame !== undefined && frame.route === undefined) {
        const entry = entryForState(frame.state, mode);
        const options = navigatorOptions(entry);
        await withDeadline(
          {
            deadline: LEARN_DECK_HISTORY_STEP_DEADLINE_MS,
            operation: "Learn deck history card preparation",
          },
          () => preparePageNavigatorQueries(queryClient, options.queries),
        );
        // A newer Back/Forward event wins over the card whose queries just completed.
        if (targetRef.current !== frame.id) {
          return;
        }
        // Keep the already prepared underlay. History only changes which frame has focus.
        const restored = { ...frame.state, transition: { phase: "idle" as const } };
        frame.state = restored;
        setShown(restored);
        setRestore({ enterFrom, state: restored });
      }
      needsRestore.current = false;
      deferredNavigation.current = false;
      setRevision((r) => r + 1);
    } catch (error) {
      setError(
        isDeadlineExceededError(error)
          ? "This card is taking longer to load. Please retry."
          : error instanceof Error
            ? error.message
            : "Could not restore this review.",
      );
    } finally {
      busy.current = false;
      setPendingStep(null);
      releaseRatingLatch(latch);
    }
  }, [controller, mode, navigatorOptions, queryClient, targetRef]);
  const reconcileRef = useLatest(reconcile);
  useEffect(() => {
    if (error !== null || isPending) {
      return;
    }
    void reconcileRef.current();
    // Browser Back can arrive while the rating owns the write latch. Keep that navigation
    // queued until promotion releases it; never silently drop a history event.
    const timer = setTimeout(() => {
      const target = frames.current.findIndex((frame) => frame.id === targetRef.current);
      if (target !== -1 && (target !== cursor.current || needsRestore.current)) {
        setRetry((r) => r + 1);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [error, isPending, reconcileRef, retry, targetId, targetRef]);
  useEffect(() => {
    const next = isActive
      ? learnActiveDeckCardForEntry(shown === null ? initialEntry : entryForState(shown, mode))
      : null;
    setActiveDeckCard((previous) => {
      // Face/view/history updates do not change the card advertised to shared Learn chrome.
      // Keep its identity so a reveal does not notify every active-card selector again.
      if (previous !== null && next !== null && previous.grapheme === next.grapheme) {
        if (previous.kind === "lesson" && next.kind === "lesson" && previous.part === next.part) {
          return previous;
        }
        if (
          previous.kind === "review" &&
          next.kind === "review" &&
          previous.drillType === next.drillType &&
          previous.mode === next.mode &&
          previous.contextSentence === next.contextSentence
        ) {
          return previous;
        }
      }
      return next;
    });
  }, [initialEntry, isActive, mode, setActiveDeckCard, shown]);
  // The step on screen, for study-activity bookkeeping (`StudySessionBoundary`). Native no longer
  // mirrors steps into the route, so chrome outside the deck reads this instead.
  const activeStep = isActive ? `${sessionId}:${shown === null ? "entry" : identity(shown)}` : null;
  useEffect(() => {
    setActiveDeckStep(activeStep);
  }, [activeStep, setActiveDeckStep]);
  useEffect(() => () => setActiveDeckStep(null), [setActiveDeckStep]);
  const summaryPageNavigator = useLearnSummaryPageNavigator();
  const summaryPageNavigatorRef = useLatest(summaryPageNavigator);
  const [setHasLearnFlowStartedThisSession] = useContextSelectors(
    NavContext,
    (c) => c.setHasLearnFlowStartedThisSession,
  );
  const openMap = useOpenLearnMap();
  // Start `replace`d the route it was pressed on with this deck, so there is no route behind the
  // first card. Undo there returns to where Start was pressed instead. From the map home, the map
  // opens over this card as the home again, so leaving it comes back here. From the summary card,
  // the summary replaces the deck. Nothing is persisted before the first frame, so there is no
  // review to roll back.
  const returnToSummary = useCallback(
    (entry: LearnSummaryDeckEntry) => {
      voiceStudyController.stop();
      if (entry.returnToMapHome) {
        setHasLearnFlowStartedThisSession(false);
        openMap();
        return;
      }
      busy.current = true;
      setPendingStep("back");
      void summaryPageNavigatorRef.current.replace
        .mutateAsync()
        .catch((error: unknown) => {
          showErrorToast("Could not return to the summary", error);
        })
        .finally(() => {
          busy.current = false;
          setPendingStep(null);
        });
    },
    [openMap, setHasLearnFlowStartedThisSession, summaryPageNavigatorRef, voiceStudyController],
  );
  const summaryReturn = cursor.current === 0 ? summaryEntry : null;
  const canGoBack = cursor.current > 0 || summaryReturn !== null;
  const canGoForward = cursor.current < frames.current.length - 1;
  const history = useMemo(
    () => ({
      canGoBack,
      canGoForward,
      goBack: () => {
        if (!canGoBack || busy.current) {
          return;
        }
        pendingPush.current = null;
        if (summaryReturn !== null) {
          returnToSummary(summaryReturn);
          return;
        }
        if (Platform.OS === "web") {
          router.back();
          return;
        }
        // Native keeps ONE deck route and the current step in deck state, so there is no stack
        // entry to pop: target the previous frame and let reconciliation restore it, exactly as a
        // Back would have.
        const frame = frames.current[cursor.current - 1];
        if (frame !== undefined) {
          pointAt(frame);
        }
      },
      goForward: () => {
        if (!canGoForward || busy.current || learnDeckBrowserForward()) {
          return;
        }
        const frame = frames.current[cursor.current + 1];
        if (frame === undefined) {
          return;
        }
        if (frame.route) {
          routeStamp.current = frame.id;
          void navigate(frame.route);
        } else {
          pointAt(frame);
        }
      },
      isPending,
      pendingStep,
    }),
    [
      canGoBack,
      canGoForward,
      isPending,
      navigate,
      pendingStep,
      pointAt,
      returnToSummary,
      router,
      summaryReturn,
    ],
  );
  // Android hardware Back is Undo, as it was when every step was its own stack entry. With one
  // deck route there is nothing for the navigator to pop, so the step back is taken here; at the
  // first frame it returns to the summary that opened the session, or otherwise falls through and
  // leaves the deck as usual.
  const historyRef = useLatest(history);
  useEffect(() => {
    if (!isActive || Platform.OS !== "android") {
      return;
    }
    const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
      if (!historyRef.current.canGoBack) {
        return false;
      }
      historyRef.current.goBack();
      return true;
    });
    return () => subscription.remove();
  }, [historyRef, isActive]);
  return (
    <LearnDeckHistoryContext.Provider value={history}>
      <View
        accessibilityElementsHidden={!isActive}
        className={
          isActive || isUnderMap
            ? "absolute inset-0 z-10"
            : previewHeight !== null
              ? "absolute inset-0"
              : "absolute inset-0 opacity-0"
        }
        importantForAccessibility={isActive ? "auto" : "no-hide-descendants"}
        pointerEvents={isActive ? "auto" : "none"}
      >
        {isActive || isUnderMap || previewHeight !== null ? <LearnMapCardsBackdrop /> : null}
        {isActive ? (
          <PrimaryControlsStripTitle>
            {shown?.current.item.kind === "review" ? "Review" : "Learn"}
          </PrimaryControlsStripTitle>
        ) : null}
        <LearnFlashcardDeck
          entry={initialEntry}
          historyRestore={restore}
          isFocused={isActive}
          isHistoryPending={isPending || error !== null}
          isLearnFocused={isLearnFocused}
          levelIndex={levelIndex}
          onActionSaved={onActionSaved}
          onCurrentChange={checkpoint}
          onRouteExit={onRouteExit}
          ownsToolbar={ownsToolbar}
          previewCardHeight={previewHeight}
        />
        {error !== null ? (
          <View
            className="absolute inset-0 items-center justify-center bg-background/70"
            testID="learn-history-error"
          >
            <Text>{error}</Text>
            <Button
              onPress={() => {
                setError(null);
                setRetry((r) => r + 1);
              }}
            >
              <Text>Retry</Text>
            </Button>
          </View>
        ) : isPending ? (
          // The wait shows on Undo or Redo itself, only when it lasts (the card it brings back is
          // usually ready at once). The deck stays as it is, untouchable, until the step lands.
          <View className="absolute inset-0" testID="learn-history-pending" />
        ) : null}
      </View>
    </LearnDeckHistoryContext.Provider>
  );
}
